import React, { useEffect } from "react";
import Tags from "../../components/data_entry/tags";
import { QuestionProps } from "../survey";
import styled from "styled-components";
import QuestionLabel from "./components/question_label";
import TextArea from "../../components/data_entry/text_area";
import { Option, getOptionObjectFromText, getOptionOid, getOptionText } from "./question_helpers";
import InputAutocomplete from "../../components/data_entry/input_autocomplete";
import SelectQuestionWrapper from "../../components/base_ui/higher_order_components/questions/select_question_wrapper";

export interface Answer {
  writeIn?: string;
  followUpResponse?: string;
  selectedOptions: (string | Option | undefined)[];
}

export interface Customizations {
  prompt: string;
  options: (string | Option)[];
  followUp?: {
    option: string;
    prompt: string;
    includeOpenResponse: boolean;
    placeholder?: string;
  };
  numberToChoose?: number;
  disableTagCreation?: boolean;
  description?: string;
  placeholder?: string;
  optional?: boolean;
}

const Question = styled.div`
  .counterpart {
    margin-top: ${({ theme }) => theme.deprecatedSpacing.component.three};
  }
  .label {
    .label-text {
      text-transform: none;
      display: inline-block;
      font-weight: ${({ theme }) => theme.font.weight.light};
      font-size: 18px;
      white-space: pre-wrap;
      color: ${({ theme }) => theme.deprecatedColors.gray.four};
    }
  }
`;

const StyledFollowUp = styled.div`
  padding-top: 2em;
`;

const StyledDropdown = styled(InputAutocomplete)`
  .label {
    display: initial;
  }

  .inputWrapper {
    margin-top: 24px;
  }

  .text-input .input-description {
    display: block;
    font-weight: 500;
    font-size: 15px;
    margin-top: 17px;
    margin-bottom: 0;
    color: ${({ theme }) => theme.deprecatedColors.accent.four};
  }

  .input-options {
    max-height: 200px;
    overflow: scroll;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function wrapDropdownStyles(Component: any) {
  return styled(Component)``;
}
const StyledDropdownTags = wrapDropdownStyles(Tags);

export default function DropdownComponent({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, Customizations>) {
  const label = customizations.prompt;
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const disableTagCreation = customizations.disableTagCreation;
  const secondaryText = customizations.description;
  const placeholder = customizations.placeholder;
  const followUp = customizations.followUp;
  const numberToChoose = customizations.numberToChoose;
  const options = customizations.options;
  const answerIsValid = optional || (currentAnswer?.selectedOptions.length || 0) > 0;
  const selectedOptions = currentAnswer?.selectedOptions || [];

  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  const tagItemList = options.map((option) => {
    return { value: getOptionText(option) ?? "", label: getOptionText(option) };
  });

  const deprecatedDropdownComponent = (
    <Question className="multi-select-dropdown">
      {(!numberToChoose || numberToChoose > 1) && (
        <StyledDropdownTags
          placeholder={placeholder ? placeholder : "Click or search here..."}
          description={secondaryText}
          adornmentIcon="chevron-down"
          label={label}
          disableTagCreation={disableTagCreation}
          questionIsOptional={optional}
          availableTags={selectedOptions.length === numberToChoose ? [] : tagItemList}
          value={selectedOptions.map((option) => getOptionText(option))}
          onTagDelete={(deletedTag: string) => {
            updateAnswer({
              selectedOptions: selectedOptions.filter(
                (option) => getOptionText(option) !== deletedTag,
              ),
            });
          }}
          onTagSelect={(selectedTagString: string) => {
            updateAnswer({
              selectedOptions: [
                ...selectedOptions,
                getOptionObjectFromText(selectedTagString, customizations.options),
              ],
            });
          }}
        />
      )}
      {numberToChoose === 1 && (
        <StyledDropdown
          placeholder={placeholder ? placeholder : "Click or search here..."}
          label={label}
          description={secondaryText}
          optional={optional}
          adornmentIcon="chevron-down"
          availableOptions={tagItemList}
          clearOnSelect={false}
          onSelect={(selectedTag: string) => {
            updateAnswer({
              selectedOptions: [getOptionObjectFromText(selectedTag, customizations.options)],
            });
          }}
          onChange={(selectedTag) => {
            if (!selectedTag) return updateAnswer({ selectedOptions: [] });

            const option = getOptionObjectFromText(selectedTag.trim(), customizations.options);
            const answers = option ? [option] : [];
            updateAnswer({ selectedOptions: answers });
          }}
        />
      )}
      {followUp &&
        selectedOptions.map((option) => getOptionText(option)).includes(followUp.option) && (
          <StyledFollowUp>
            <QuestionLabel
              label={followUp.prompt}
              className="question-label"
              questionIsOptional={true}
            />
            {followUp.includeOpenResponse && (
              <TextArea
                data-test="select-follow-up-input"
                rows={3}
                placeholder={followUp.placeholder ? followUp.placeholder : undefined}
                onChange={({ currentTarget: { value: updatedResponse } }) =>
                  updateAnswer({
                    ...currentAnswer,
                    selectedOptions: selectedOptions,
                    followUpResponse: updatedResponse,
                  })
                }
                value={currentAnswer ? currentAnswer.followUpResponse : ""}
              />
            )}
          </StyledFollowUp>
        )}
    </Question>
  );
  const requiredErrorMessage = "Please complete the required field.";
  const multiselectComponent = (
    <>
      <SelectQuestionWrapper
        supplementalQuestionPrompt={label}
        placeholder={placeholder ? placeholder : "Select..."}
        optional={optional}
        secondaryText={secondaryText}
        value={
          selectedOptions.length > 0
            ? {
                id: getOptionOid(selectedOptions[0]) || "",
                label: getOptionText(selectedOptions[0]),
              }
            : undefined
        }
        options={options.map((option) => {
          const optionLabel = getOptionText(option);
          return {
            id: getOptionOid(option) || "",
            label: getOptionText(option) || "",
            value: selectedOptions.includes(getOptionObjectFromText(optionLabel || "", options)),
          };
        })}
        onChange={(newAnswer) => {
          updateAnswer({
            selectedOptions: newAnswer
              ? [getOptionObjectFromText(newAnswer.label as string, customizations.options)]
              : [],
          });
        }}
        setAbleToSubmit={setAbleToSubmit}
        errorMessage={!answerIsValid && showValidation ? requiredErrorMessage : undefined}
        followUpPrompt={followUp?.prompt}
        followUpIsOptional={true}
        showFollowUp={followUp && getOptionText(selectedOptions[0]) === followUp?.option}
        dataTest="survey-question-multi-select"
      ></SelectQuestionWrapper>
    </>
  );

  return showRefreshedComponents ? multiselectComponent : deprecatedDropdownComponent;
}
